import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import otp from '../../../assets/otp.png';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLoginMutation, useSendOtpMutation, useVerifyOtpMutation } from '../AuthApi';
import {
  Button,
  Input,
  VStack,
} from '../../Shared/Components/ui';
import { useAuth } from '../../Shared/Hooks/useAuth';
import { toast } from 'react-toastify';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { cn, generateSessionId, isSafeRedirectPath } from '../../Shared/lib/utils';
import { useInstance } from '../../Shared/Hooks/useInstance';
import AuthShell from '../Shared/AuthShell';
import AuthCard from '../Shared/AuthCard';
import AuthFooter from '../Shared/AuthFooter';

interface OtpFormData {
  username: string;
  otp: string;
}

interface PassedData {
  username: string;
  password: string;
  sessionId: string;
  contactId: string;
  destination: {
    channel: 'sms' | 'whatsapp' | 'email';
    maskedValue: string;
  };
  from?: { pathname: string; search?: string };
}

const channelLabel = (channel: PassedData['destination']['channel']) => {
  if (channel === 'email') return 'Email';
  if (channel === 'whatsapp') return 'WhatsApp';
  return 'SMS';
};

const Otp = () => {
  const { instance } = useInstance();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OtpFormData>();
  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [showRetry, setShowRetry] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const passedData = location.state as PassedData | null;
  const { authenticate } = useAuth();
  const [login] = useLoginMutation();
  const [sendOtp] = useSendOtpMutation();
  const [newSessionId, setNewSessionId] = useState(passedData?.sessionId ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startRetryTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowRetry(false);
    timerRef.current = setTimeout(() => setShowRetry(true), 90000);
  }, []);

  useEffect(() => {
    startRetryTimer();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [startRetryTimer]);

  const handleRetry = async () => {
    if (!passedData) {
      navigate('/login');
      return;
    }

    try {
      const sessionId = generateSessionId();
      setIsSubmitting(true);

      await login({
        username: passedData.username,
        password: passedData.password,
        sessionId,
      }).unwrap();

      await sendOtp({
        sessionId,
        contactId: passedData.contactId,
      }).unwrap();

      setNewSessionId(sessionId);
      startRetryTimer();
      toast.success(`New code sent to ${passedData.destination.maskedValue}.`);
    } catch (error) {
      if (error && (error as FetchBaseQueryError).status === 'FETCH_ERROR') {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Failed to resend code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<OtpFormData> = async data => {
    if (!passedData) {
      console.error('No session data found.');
      return;
    }

    try {
      const response: any = await verifyOtp({
        username: passedData.username,
        otp: data.otp,
        sessionId: newSessionId,
      }).unwrap();
      authenticate(response);
      toast.success('OTP verified successfully. You are now logged in.');
      const destinationPath = passedData.from?.pathname;
      const destinationSearch = passedData.from?.search ?? '';
      navigate(isSafeRedirectPath(destinationPath) ? destinationPath + destinationSearch : '/dashboard');
    } catch (error) {
      if (error && (error as FetchBaseQueryError).status === 'FETCH_ERROR') {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Invalid OTP. Please try again.');
      }
    }
  };

  return (
    <AuthShell
      side={{
        title: (
          <>
            Secure Your <br />
            <span className="text-teal-accent">Account Access</span>
          </>
        ),
        text: "Multi-factor authentication ensures your school's data remains safe and protected.",
      }}
    >
      <AuthCard logo={instance?.logo}>
          <div className="text-center mb-10 pt-6">
            <h1 className="text-2xl tablet:text-3xl bricolage-grotesque-extrabold text-[#111827] mb-2 flex items-center justify-center gap-2">
              <img src={otp} alt="OTP" className="w-8 h-8 opacity-80" />
              Verify identity
            </h1>
            {passedData?.destination ? (
              <p className="text-gray-secondary text-sm tablet:text-base manrope-medium">
                Code sent to{' '}
                <span className="text-teal-primary font-bold">{passedData.destination.maskedValue}</span>
                {' '}via {channelLabel(passedData.destination.channel)}
              </p>
            ) : (
              <p className="text-gray-secondary text-sm tablet:text-base manrope-medium">
                Enter the code sent to your device
              </p>
            )}
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={4}>
              <Input
                label="OTP Code"
                className="h-14 text-center text-2xl tracking-[0.5em] font-bold"
                placeholder="000000"
                {...register('otp', { required: 'OTP is required' })}
                error={errors?.otp}
              />
              <p className="text-[11px] text-slate-400 text-center leading-relaxed font-medium">
                Never share this code with anyone. <br /> Skuulbe will never ask for your verification code.
              </p>
            </VStack>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full h-14 text-lg"
              isLoading={isLoading}
            >
              Verify & Proceed
            </Button>

            {showRetry && (
              <div className="pt-4 text-center">
                <p className="text-slate-500 text-sm font-medium">
                  Didn't receive the code?{' '}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'text-teal-primary hover:underline font-bold p-0 min-h-0 h-auto',
                      isSubmitting && 'opacity-50 cursor-not-allowed'
                    )}
                    onClick={handleRetry}
                    disabled={isSubmitting}
                  >
                    Resend Code
                  </Button>
                </p>
              </div>
            )}

            <AuthFooter displayName={instance?.displayName} divider />
          </form>
      </AuthCard>
    </AuthShell>
  );
};

export default Otp;
